/**
 * useSubjectFeesEntryScreen.js
 * ----------------------------
 * Hook for Subjectwise Fees create/edit form screen using shared useMasterEntry.
 */
import { useState, useEffect, useMemo } from 'react';
import { useMasterEntry } from '../../common/useMasterEntry';
import * as subjectFeesApi from '../api/subjectFeesApi';
import { getCourseOptions } from '../../courses/api/coursesApi';
import { getSubjectOptions } from '../../subjects/api/subjectsApi';

export function useSubjectFeesEntryScreen() {
  const [courseOptions, setCourseOptions] = useState([]);
  const [subjectOptions, setSubjectOptions] = useState([]);

  useEffect(() => {
    let isMounted = true;
    async function loadOptions() {
      try {
        const [cOpts, sOpts] = await Promise.all([
          getCourseOptions().catch(() => []),
          getSubjectOptions().catch(() => []),
        ]);
        if (isMounted) {
          setCourseOptions(cOpts || []);
          setSubjectOptions(sOpts || []);
        }
      } catch (err) {
        console.error('Failed to load course/subject options:', err);
      }
    }
    loadOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  const master = useMasterEntry({
    entityName: 'Subjectwise Fee',
    apiClient: subjectFeesApi.subjectFeesClient,
    fetchDetail: subjectFeesApi.getSubjectFeesDetail,
    createItem: subjectFeesApi.createSubjectFees,
    updateItem: subjectFeesApi.updateSubjectFees,
    initialForm: {
      ayear: '2024-2025',
      course_id: '',
      subject_id: '',
      fees_admission: 0,
      fees_tuition: 0,
      fees_courseware: 0,
    },
    listPath: '/masters/subject-fees/list',
    validate: (form) => {
      if (!form.ayear || !form.ayear.trim()) return 'Please select Academic Year.';
      if (!form.course_id) return 'Please select Course.';
      if (!form.subject_id) return 'Please select Subject.';
      const adm = parseFloat(form.fees_admission) || 0;
      const tui = parseFloat(form.fees_tuition) || 0;
      const cou = parseFloat(form.fees_courseware) || 0;
      if (adm < 0 || tui < 0 || cou < 0) return 'Fee amounts cannot be negative.';
      if (adm + tui + cou <= 0) return 'Please enter fee amount.';
      return null;
    },
    transformPayload: (form, operationalStatus) => ({
      ayear: form.ayear.trim(),
      course_id: Number(form.course_id),
      subject_id: Number(form.subject_id),
      fees_admission: parseFloat(form.fees_admission) || 0,
      fees_tuition: parseFloat(form.fees_tuition) || 0,
      fees_courseware: parseFloat(form.fees_courseware) || 0,
      status: operationalStatus === 'active' ? 'Active' : 'Archived',
    }),
    transformDetail: (detail) => ({
      form: {
        ayear: detail.ayear || '',
        course_id: detail.course_id ? String(detail.course_id) : '',
        subject_id: detail.subject_id ? String(detail.subject_id) : '',
        fees_admission: detail.fees_admission ?? 0,
        fees_tuition: detail.fees_tuition ?? 0,
        fees_courseware: detail.fees_courseware ?? 0,
      },
      operationalStatus: detail.status === 'Archived' ? 'inactive' : 'active',
    }),
  });

  const totalFees = useMemo(() => {
    const adm = parseFloat(master.state.form.fees_admission) || 0;
    const tui = parseFloat(master.state.form.fees_tuition) || 0;
    const cou = parseFloat(master.state.form.fees_courseware) || 0;
    return adm + tui + cou;
  }, [master.state.form.fees_admission, master.state.form.fees_tuition, master.state.form.fees_courseware]);

  return {
    ...master,
    state: {
      ...master.state,
      courseOptions,
      subjectOptions,
      totalFees,
    },
  };
}
